import { useMemo } from 'react';
import { TextField } from '@/shared/components/TextField';
import { useAddUser } from '../../hooks/useAddUser';
import CreateLayout from '@/shared/layout/createLayout';
import type { AddUserDetails } from '../../types/addUserType';

const AddAdminHeader = () => (
  <p className="text-titleLarge text-neutral-10 ">Dodaj administratora</p>
);

type AddAdminFormProps = {
  formData: AddUserDetails;
  errors: AddUserDetails;
  isSubmitting: boolean;
  handleNameChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleEmailChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handlePhoneChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress1Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress2Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCityChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleStateChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleZipChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
};

const AddUser = () => {
  const {
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    handleSubmit,
    handleCancel,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,
    formData,
    errors,
    isSubmitting,
    handleNameChange,
    handleEmailChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
  } = useAddUser();

  const AddAdminForm = useMemo(
    () =>
      function AddAdminForm({
        formData,
        errors,
        isSubmitting,
        handleNameChange,
        handleEmailChange,
        handlePhoneChange,
        handleAddress1Change,
        handleAddress2Change,
        handleCityChange,
        handleStateChange,
        handleZipChange,
      }: AddAdminFormProps) {
        return (
          <>
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Podstawowe dane
              </p>
              <div className="flex gap-4 ">
                <TextField
                  fullWidth={true}
                  label="Imię i nazwisko"
                  autoTrimMaxLength={200}
                  value={formData.name}
                  onChange={handleNameChange}
                  error={Boolean(errors.name)}
                  errorMessage={errors.name}
                  disabled={isSubmitting}
                  autoFocus={true}
                />
                <TextField
                  fullWidth={true}
                  label="Adres e-mail"
                  autoTrimMaxLength={100}
                  value={formData.email}
                  onChange={handleEmailChange}
                  error={Boolean(errors.email)}
                  errorMessage={errors.email}
                  disabled={isSubmitting}
                />
                <TextField
                  fullWidth={true}
                  label="Numer telefonu"
                  type="tel"
                  maxLength={9}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  error={Boolean(errors.phone)}
                  errorMessage={errors.phone}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Dane adresowe
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <TextField
                    fullWidth={true}
                    label="Adres (linia 1)"
                    autoTrimMaxLength={200}
                    value={formData.address1}
                    onChange={handleAddress1Change}
                    error={Boolean(errors.address1)}
                    errorMessage={errors.address1}
                    disabled={isSubmitting}
                  />
                  <TextField
                    fullWidth={true}
                    label={
                      <>
                        Adres (linia 2)
                        <span className="text-bodySmall font-normal">
                          {' '}
                          (Opcjonalne)
                        </span>
                      </>
                    }
                    dataTestId="Adres (linia 2)"
                    autoTrimMaxLength={200}
                    value={formData.address2}
                    onChange={handleAddress2Change}
                    disabled={isSubmitting}
                  />
                  <TextField
                    fullWidth={true}
                    label="Miejscowość"
                    autoTrimMaxLength={200}
                    value={formData.city}
                    onChange={handleCityChange}
                    error={Boolean(errors.city)}
                    errorMessage={errors.city}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex gap-4">
                  <TextField
                    fullWidth={true}
                    label="Województwo"
                    autoTrimMaxLength={200}
                    value={formData.state}
                    onChange={handleStateChange}
                    error={Boolean(errors.state)}
                    errorMessage={errors.state}
                    disabled={isSubmitting}
                  />
                  <TextField
                    fullWidth={true}
                    label={
                      <>
                        Kod pocztowy
                        <span className="text-bodySmall font-normal">
                          {' '}
                          (Opcjonalne)
                        </span>
                      </>
                    }
                    dataTestId="Kod pocztowy"
                    maxLength={200}
                    value={formData.zip}
                    onChange={handleZipChange}
                    error={Boolean(errors.zip)}
                    errorMessage={errors.zip}
                    disabled={isSubmitting}
                  />
                  <TextField
                    fullWidth={true}
                    label="Kraj"
                    maxLength={200}
                    value={formData.country}
                    disabled={true}
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Dostęp i uprawnienia
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 w-[32%]">
                  <TextField
                    fullWidth={true}
                    label="Rola"
                    maxLength={200}
                    disabled={true}
                    value={formData.role}
                  />
                </div>
              </div>
            </div>
          </>
        );
      },
    []
  );

  return (
    <CreateLayout
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isSnackbarOpen}
      handleCloseSnackbar={handleCloseSnackbar}
      snackbarError={snackbarError}
      Header={AddAdminHeader}
    >
      <AddAdminForm
        formData={formData}
        errors={errors}
        isSubmitting={isSubmitting}
        handleNameChange={handleNameChange}
        handleEmailChange={handleEmailChange}
        handlePhoneChange={handlePhoneChange}
        handleAddress1Change={handleAddress1Change}
        handleAddress2Change={handleAddress2Change}
        handleCityChange={handleCityChange}
        handleStateChange={handleStateChange}
        handleZipChange={handleZipChange}
      />
    </CreateLayout>
  );
};
export default AddUser;
